
import React from "react";
import KpiCard from "../components/dashboard/KpiCard";
import RevenueLine from "../components/dashboard/RevenueLine";
import TopCategoriesDonut from "../components/dashboard/TopCategoriesDonut";
import OrderTypesList from "../components/dashboard/OrderTypesList";
import OrdersBar from "../components/dashboard/OrdersBar";
import SideBarMenu from "../components/dashboard/SideBarMenu";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";


import {
  fetchDashboardL1,
  fetchRevenueTrendL2,
   getDashboardL3 as fetchOrdersOverviewL3,
} from "../services/dashboard.service";
import { getGranularity } from "../utils/rangeUtils";
import { FiFileText, FiUsers } from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";

export default function Dashboard() {
  const user = getUser() || DEV_USER;
  const ORG_ID = user.restaurant_id;
  const LOCATION = user.location;


  const [l2Range, setL2Range] = React.useState("this_month");
  const [l3Range, setL3Range] = React.useState("this_month");
  const l2Granularity = getGranularity(l2Range);
  const l3Granularity = getGranularity(l3Range);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [kpis, setKpis] = React.useState(null);
  const [topCategories, setTopCategories] = React.useState({ label: "", seriesData: [] });
  const [orderTypes, setOrderTypes] = React.useState({ label: "", items: [] });
  const [revenueTrend, setRevenueTrend] = React.useState({ categories: [], series: [] });
  const [ordersOverview, setOrdersOverview] = React.useState({
    label: "",
    categories: [],
    series: [{ name: "Orders", data: [] }],
  });

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [l1, l2, l3] = await Promise.all([
        fetchDashboardL1({ orgId: ORG_ID, location: LOCATION, range: "1m" }),
        fetchRevenueTrendL2({
          orgId: ORG_ID,
          location: LOCATION,
          range: l2Range,
          granularity: l2Granularity,
        }),
        fetchOrdersOverviewL3({
          orgId: ORG_ID,
          location: LOCATION,
          range: l3Range,
          granularity: l3Granularity,
        }),
      ]);
      setKpis(l1.kpis);
      setTopCategories(l1.topCategories);
      setOrderTypes(l1.orderTypes);
      setRevenueTrend(l2);
      setOrdersOverview(l3);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [l2Range, l2Granularity, l3Range, l3Granularity, ORG_ID, LOCATION]);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="page page--dashboard">
      <div className="dash-wrap">
        <main className="dash-main">
          {error && (
            <div className="error-box">
              <b>Couldn’t load dashboard</b>
              <div className="muted">{String(error?.message || error)}</div>
            </div>
          )}

          <div className="dash-grid" aria-busy={loading ? "true" : "false"}>
            {/* KPIs */}
            <section className="dash-card kpi-1">
              <KpiCard
                icon={<FiFileText size={20} />}
                title="Total Orders"
                value={kpis?.orders?.value ?? 0}
                deltaPct={kpis?.orders?.deltaPct ?? 0}
              />
            </section>

            <section className="dash-card kpi-2">
              <KpiCard
                icon={<FiUsers size={22} />}
                title="Total Customer"
                value={kpis?.customers?.value ?? 0}
                deltaPct={kpis?.customers?.deltaPct ?? 0}
              />
            </section>

            <section className="dash-card kpi-3">
              <KpiCard
                icon={<FaRupeeSign size={22} />}
                title="Total Revenue"
                value={kpis?.revenue?.value ?? 0}
                deltaPct={kpis?.revenue?.deltaPct ?? 0}
                isMoney
              />
            </section>

            {/* Revenue (L2) */}
            <section className="dash-card revenue">
              <RevenueLine data={revenueTrend} range={l2Range} onRangeChange={setL2Range} />
            </section>

            {/* Top Categories (L1) */}
            <section className="dash-card donut">
              <TopCategoriesDonut data={topCategories} />
            </section>

            {/* Order Types (L1) */}
           <section className="dash-card orders-bar">
              <OrdersBar data={ordersOverview} range={l3Range} onRangeChange={setL3Range} />
            </section>

            {/* Orders Overview (L3) */}
             <section className="dash-card types-list">
              <OrderTypesList data={{ label: orderTypes.label, items: orderTypes.items }} />
            </section>
          </div>
        </main>

        {/* unchanged */}
        <div className="dash-aside">
          <SideBarMenu />
        </div>
      </div>
    </div>
  );
}
